import { Link } from 'react-router-dom';
import { ArrowRight, GitBranch } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface ResubmitBannerProps {
  /** If this job is a revision, the parent job ID (FR-078) */
  parentJobId?: string | null;
  /** If this job was later revised by a child job, that child's ID */
  childJobId?: string | null;
  /** Optional 1-based version number for this job */
  version?: number | null;
  /**
   * Optional ordered list of all ancestor / descendant job IDs to render
   * a compact timeline when the chain has 3+ entries:
   *   [grandparentId, parentId, currentId, childId, …]
   * The current job ID must be included.
   */
  revisionChain?: string[];
  /** ID of the current job (required when revisionChain is provided) */
  currentJobId?: string;
}

function shortId(id: string): string {
  return id.slice(0, 8);
}

/**
 * FR-078 lineage banner.
 *
 * - Child job: shows "Revision of Job #XXX" + "View original" link
 * - Parent job with child: shows "Revised by Job #YYY" + "View latest" link
 * - Version badge when version > 1
 * - Compact horizontal timeline when revisionChain has 3+ entries
 */
export function ResubmitBanner({
  parentJobId,
  childJobId,
  version,
  revisionChain,
  currentJobId,
}: ResubmitBannerProps) {
  if (!parentJobId && !childJobId) return null;

  const showTimeline =
    revisionChain && revisionChain.length >= 3 && currentJobId;

  return (
    <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30">
      <GitBranch className="h-4 w-4 text-blue-600 dark:text-blue-400" aria-hidden="true" />
      <AlertDescription className="space-y-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-blue-700 dark:text-blue-300">
          {/* Version badge */}
          {version != null && version > 1 && (
            <Badge
              variant="outline"
              className="border-blue-300 bg-blue-100 text-blue-700 dark:border-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
              aria-label={`Version ${version}`}
            >
              v{version}
            </Badge>
          )}

          {/* Child → parent direction */}
          {parentJobId && (
            <span>
              Revision of{' '}
              <Link
                to={`/jobs/${parentJobId}`}
                className="font-medium underline underline-offset-2 hover:no-underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
              >
                Job #{shortId(parentJobId)}
              </Link>
            </span>
          )}

          {/* Parent → child direction */}
          {childJobId && (
            <span>
              Revised by{' '}
              <Link
                to={`/jobs/${childJobId}`}
                className="font-medium underline underline-offset-2 hover:no-underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
              >
                Job #{shortId(childJobId)}
              </Link>
            </span>
          )}

          {/* Quick-nav buttons */}
          {parentJobId && (
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 text-blue-700 dark:text-blue-300 underline-offset-2"
              asChild
            >
              <Link to={`/jobs/${parentJobId}`}>View original</Link>
            </Button>
          )}
          {childJobId && (
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 text-blue-700 dark:text-blue-300 underline-offset-2"
              asChild
            >
              <Link to={`/jobs/${childJobId}`}>View latest</Link>
            </Button>
          )}
        </div>

        {/* Compact horizontal timeline for chains of 3+ */}
        {showTimeline && (
          <ol
            className="flex flex-wrap items-center gap-1 text-xs text-blue-600 dark:text-blue-400"
            aria-label="Revision chain"
          >
            {revisionChain.map((id, idx) => {
              const isCurrent = id === currentJobId;
              return (
                <li key={id} className="flex items-center gap-1">
                  {idx > 0 && (
                    <ArrowRight className="h-3 w-3 shrink-0" aria-hidden="true" />
                  )}
                  {isCurrent ? (
                    <span
                      className="rounded bg-blue-200 px-1.5 py-0.5 font-semibold dark:bg-blue-800"
                      aria-current="page"
                    >
                      #{shortId(id)}
                    </span>
                  ) : (
                    <Link
                      to={`/jobs/${id}`}
                      className="rounded px-1 hover:underline focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      #{shortId(id)}
                    </Link>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </AlertDescription>
    </Alert>
  );
}
